import { Stack, Box, CircularProgress } from '@mui/material';
import { DragDropContext, DropResult } from 'react-beautiful-dnd';
import PipelineColumn from './pipeline-column';
import { ColumnId, getColumnsForSegment, PipelineSegment } from './pipeline-utils';
import usePipelineStore from 'stores/pipeline-store';
import { updatePipelineStatus } from 'API/funnel-contact-management';
import { useUserData } from 'hooks/use-user-data';
import { useEffect, useState } from 'react';
import useContactDrawer from 'features/co-pilot/campaign-builder/use-contact-drawer';
import ContactDrawer from 'features/co-pilot/campaign-builder/campaign-building-panel/campaign-pipeline/contact-preview-drawer/contact-drawer';
import { ProductGroups } from 'enums';
import { FunnelContact } from 'features/co-pilot/co-pilot.types';

interface Props {
  segment: PipelineSegment;
  funnelId: string | null;
  allContacts: FunnelContact[];
  onStatusUpdated?: () => void;
}

const PipelineBoard: React.FC<Props> = ({ segment, funnelId, allContacts, onStatusUpdated }) => {
  const {
    columns,
    startMove,
    confirmMove,
    rollbackMove,
  } = usePipelineStore((s) => ({
    columns: s.columns,
    contacts: s.contacts,
    startMove: s.startMove,
    confirmMove: s.confirmMove,
    rollbackMove: s.rollbackMove,
  }));

  const { token } = useUserData();

  const columnOrder: ColumnId[] = getColumnsForSegment(segment);

  // single contact drawer hook for entire board
  const {
    contactDrawer,
    handleOpenContactDrawer,
    handleGetNextContact,
    handleGetPrevContact,
    handleUpdateDrawerContactIsActive,
    handleCloseContactDrawer,
  } = useContactDrawer(undefined, allContacts);

  const [flashId, setFlashId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const showOverlay = busy;

  const onDragEnd = async (result: DropResult) => {
    if (!result.destination) return;

    const srcCol = result.source.droppableId as ColumnId;
    const dstCol = result.destination.droppableId as ColumnId;
    if (srcCol === dstCol && result.source.index === result.destination.index) return;

    // create snapshot
    const snapshot = JSON.parse(JSON.stringify(columns));
    startMove(snapshot);

    const srcList = Array.from(columns[srcCol]);
    const [moved] = srcList.splice(result.source.index, 1);
    const dstList = Array.from(columns[dstCol]);
    dstList.splice(result.destination.index, 0, moved);

    const newCols = { ...columns, [srcCol]: srcList, [dstCol]: dstList };
    confirmMove(newCols);

    // flash card visual feedback once confirmed
    setFlashId(moved.profileIds?.linkedin || moved.linkedInURL || null);
    setTimeout(() => setFlashId(null), 400);

    // call backend only if we have the required identifiers
    const effectiveFunnelId = funnelId && funnelId !== '__overall__' ? funnelId : (moved as any).originFunnelId;
    const profileId = moved.profileIds?.linkedin || moved.profileIds?.sales_navigator || moved.profileIds?.vanity;

    if (!token || !effectiveFunnelId || !profileId) {
      console.warn('Skipped pipeline status update: missing identifiers', { effectiveFunnelId, profileId, moved });
      return;
    }

    try {
      setBusy(true);
      await updatePipelineStatus({
        funnelId: effectiveFunnelId,
        profileId: profileId,
        pipeline: segment,
        status: dstCol,
        value: true,
      });
      console.log('Pipeline status updated', {
        funnelId: effectiveFunnelId,
        profileId,
        pipeline: segment,
        status: dstCol,
      });
      onStatusUpdated?.();
    } catch (e) {
      console.error('Failed to update pipeline status', e);
      rollbackMove();
    } finally {
      setBusy(false);
    }
  };

  // auto-scroll window near edges while dragging
  const onDragUpdate = (update: any) => {
    if (!update.destination) return;
    const y = update.client?.y || 0;
    const edge = 60;
    if (y < edge) window.scrollBy({ top: -10, behavior: 'smooth' });
    if (window.innerHeight - y < edge) window.scrollBy({ top: 10, behavior: 'smooth' });
  };

  useEffect(() => { }, [columns]);

  return (
    <Box sx={{ position: 'relative' }}>
      {showOverlay && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.1)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}
      <DragDropContext onDragEnd={onDragEnd} onDragUpdate={onDragUpdate}>
        <Stack direction={'row'} gap={'12px'} sx={{ overflowX: 'auto', alignItems: 'stretch', pointerEvents: showOverlay ? 'none' : 'auto' }}>
          {columnOrder.map((colId) => (
            <PipelineColumn
              key={colId}
              id={colId}
              title={colId}
              contacts={columns[colId] || []}
              funnelId={funnelId}
              handleOpenContactDrawer={handleOpenContactDrawer}
              flashId={flashId}
            />
          ))}
        </Stack>
      </DragDropContext>
      {/* Contact Drawer */}
      <ContactDrawer
        open={contactDrawer.open}
        productGroup={ProductGroups.LEAD_BOOSTER}
        funnelId={contactDrawer.funnelId}
        contactIndex={contactDrawer.contactIndex}
        profileId={contactDrawer.profileId}
        aiJudgement={contactDrawer.aiJudgement}
        aiJudgementExplanation={contactDrawer.aiJudgementExplanation}
        assessment={contactDrawer.assessment}
        percentiles={contactDrawer.percentiles}
        isActive={contactDrawer.isActive}
        contactDetails={contactDrawer.contactDetails}
        aboutContact={contactDrawer.aboutContact}
        aboutCompany={contactDrawer.aboutCompany}
        contactList={allContacts}
        handleGetNextContact={handleGetNextContact}
        handleGetPrevContact={handleGetPrevContact}
        handleUpdateDrawerContactIsActive={handleUpdateDrawerContactIsActive}
        handleCloseDrawer={handleCloseContactDrawer}
      />
    </Box>
  );
};

export default PipelineBoard;